import React, { Component } from 'react';
import { throttle } from 'lodash';
import { connect } from 'react-redux';
import styled, { keyframes } from 'styled-components/macro';
import scaleSize from '../../utils/scale-size';
import SpatialNavigationWrapper from '../../components/navigation/SpatialNavigationWrapper';
import config from '../../config/index';
import BackIcon from '../../components/Icons/back';
import dispatchKeyboardEvent from '../../utils/dispatch-keyboard-event';

const fadeIn = keyframes`
  0% {opacity: 0;}
  100% {opacity: 1;}
`;

const Arrow = styled.div`
  position: absolute;
  z-index: 99;
  animation-timing-function: linear;
  animation-name: ${fadeIn};
  animation-iteration-count: 1;
  animation-duration: 0.2s;
  cursor: pointer;

  svg {
    width: ${scaleSize(90)}px;
    height: ${scaleSize(100)}px;
  }

  &:hover {
    svg > path {
      stroke: ${({ theme }) => theme.activeColor};
      opacity: 0.8;
    }
  }
`;

const UpArrow = styled(Arrow)`
  top: ${scaleSize(40)}px;
  left: 50%;
  transform: translate3d(-50%, 0, 0) rotate(90deg);
`;

const DownArrow = styled(Arrow)`
  bottom: ${scaleSize(40)}px;
  left: 50%;
  transform: translate3d(-50%, 0, 0) rotate(-90deg);
`;

const RightArrow = styled(Arrow)`
  right: ${scaleSize(125)}px;
  top: 50%;
  transform: translate3d(0, -50%, 0) rotate(180deg);
`;

const LeftArrow = styled(Arrow)`
  left: ${scaleSize(125)}px;
  top: 50%;
  transform: translate3d(0, -50%, 0);
`;

class MouseControls extends Component {
  transitionTimeout = null;
  mouseTimeout = null;
  state = {
    up: true,
    down: true,
    left: true,
    right: true,
    isMouseActive: false,
  };

  setMouseTimeout() {
    clearTimeout(this.mouseTimeout);

    this.mouseTimeout = setTimeout(() => {
      this.deactivateMouse();
    }, config.mouseIdleTimeout);
  }

  activateMouse = () => {
    this.setState({ isMouseActive: true });
    this.setMouseTimeout();
  };

  deactivateMouse = () => {
    this.setState({ isMouseActive: false });
  };

  onMouseMove = throttle(() => {
    this.activateMouse();
    this.canFocusNext();
  }, 500);

  componentDidMount() {
    document.addEventListener('click', this.activateMouse);
    document.addEventListener('mousemove', this.onMouseMove);
    document.body.addEventListener('keyup', this.deactivateMouse);
    // setTimeout(this.canFocusNext, 500);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.activateMouse);
    document.removeEventListener('mousemove', this.onMouseMove);
    document.body.removeEventListener('keyup', this.deactivateMouse);
    clearTimeout(this.transitionTimeout);
  }

  canFocusNext = () => {
    this.transitionTimeout = setTimeout(
      () => {
        const up = SpatialNavigationWrapper.canFocusNext('up');
        const down = SpatialNavigationWrapper.canFocusNext('down');
        const right = SpatialNavigationWrapper.canFocusNext('right');
        const left = SpatialNavigationWrapper.canFocusNext('left');

        this.setState({ up, down, left, right });
      },
      config.withAnimation ? config.mainTransitionDuration : 0,
    );
  };

  onClickRight = () => {
    dispatchKeyboardEvent({ keyCode: 39, key: 'ArrowRight', which: 39 });
    this.canFocusNext();
  };

  onClickLeft = () => {
    dispatchKeyboardEvent({ keyCode: 37, key: 'ArrowLeft', which: 37 });
    this.canFocusNext();
  };

  onClickUp = () => {
    dispatchKeyboardEvent({ keyCode: 38, key: 'ArrowUp', which: 38 });
    this.canFocusNext();
  };

  onClickDown = () => {
    dispatchKeyboardEvent({ keyCode: 40, key: 'ArrowDown', which: 40 });
    this.canFocusNext();
  };

  render() {
    const { up, down, left, right, isMouseActive } = this.state;
    const { mouseControlsEnabled } = this.props;

    const active = isMouseActive && mouseControlsEnabled;

    return (
      <>
        <UpArrow
          onClick={this.onClickUp}
          show={up}
          style={{ display: active && up ? 'block' : 'none' }}
        >
          <BackIcon />
        </UpArrow>
        <DownArrow
          onClick={this.onClickDown}
          show={down}
          style={{ display: active && down ? 'block' : 'none' }}
        >
          <BackIcon />
        </DownArrow>
        <RightArrow
          onClick={this.onClickRight}
          show={right}
          style={{ display: active && right ? 'block' : 'none' }}
        >
          <BackIcon />
        </RightArrow>
        <LeftArrow
          onClick={this.onClickLeft}
          show={left}
          style={{ display: active && left ? 'block' : 'none' }}
        >
          <BackIcon />
        </LeftArrow>
      </>
    );
  }
}

const mapStateToProps = ({ app }) => ({ mouseControlsEnabled: app.mouseControlsEnabled });

export default connect(
  mapStateToProps,
  null,
)(MouseControls);
